import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { X, Copy, RefreshCw, Check, Loader2, Code2, Pencil, Save, GitPullRequest } from 'lucide-react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight, oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { apiClient } from '../../api/client';
import { useResolvedTheme } from '../../hooks/useResolvedTheme';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface WorkflowCodePanelProps {
  workflowName: string;
  description: string;
  definition: any;
  triggerConfig: any;
  onClose: () => void;
  regenerateTrigger?: number;
}

export function WorkflowCodePanel({
  workflowName,
  description,
  definition,
  triggerConfig,
  onClose,
  regenerateTrigger = 0,
}: WorkflowCodePanelProps) {
  const resolvedTheme = useResolvedTheme();
  const [code, setCode] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [manualRegenerate, setManualRegenerate] = useState(0);
  const [editing, setEditing] = useState(false);
  const [editedCode, setEditedCode] = useState('');
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // PR dialog state
  const [prDialogOpen, setPrDialogOpen] = useState(false);
  const [repos, setRepos] = useState<{ id: string; name: string; full_name: string; provider: 'github' | 'gitlab' }[]>([]);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [selectedRepo, setSelectedRepo] = useState('');
  const [filePath, setFilePath] = useState('');
  const [prTitle, setPrTitle] = useState('');
  const [creatingPR, setCreatingPR] = useState(false);
  const [prError, setPrError] = useState('');
  const [prSuccess, setPrSuccess] = useState<{ url: string } | null>(null);

  useEffect(() => {
    if (regenerateTrigger === 0 && manualRegenerate === 0) return;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setCode('');
    setEditedCode('');
    setEditing(false);
    setError('');
    setStreaming(true);

    apiClient.streamSDKCode(
      {
        workflow_name: workflowName || 'Untitled Workflow',
        description: description || '',
        definition,
        trigger_config: triggerConfig,
      },
      {
        onChunk: (chunk) => {
          setCode((prev) => prev + chunk);
        },
        onDone: () => {
          setStreaming(false);
        },
        onError: (msg) => {
          setError(msg);
          setStreaming(false);
        },
      },
      controller.signal,
    );

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regenerateTrigger, manualRegenerate]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [code, streaming]);

  const displayCode = editing ? editedCode : code;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(displayCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  const handleRegenerate = () => {
    setEditing(false);
    setManualRegenerate((n) => n + 1);
  };

  const handleEditToggle = () => {
    if (editing) {
      setCode(editedCode);
      setEditing(false);
    } else {
      setEditedCode(code);
      setEditing(true);
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  };

  const handleOpenPRDialog = async () => {
    setPrDialogOpen(true);
    setPrError('');
    setPrSuccess(null);
    const safeName = (workflowName || 'workflow').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    setFilePath(`workflows/${safeName}.py`);
    setPrTitle(`Add workflow: ${workflowName || 'Untitled Workflow'}`);

    if (repos.length === 0) {
      setLoadingRepos(true);
      try {
        const allRepos: typeof repos = [];
        try {
          const gh = await apiClient.getGitHubRepositories();
          for (const r of (gh.repositories || [])) {
            if (r.is_enabled) {
              allRepos.push({ id: r.id.toString(), name: r.repo_full_name, full_name: r.repo_full_name, provider: 'github' });
            }
          }
        } catch { /* no GitHub */ }
        try {
          const gl = await apiClient.getGitLabRepositories();
          for (const r of (gl.repositories || [])) {
            if (r.is_enabled) {
              allRepos.push({ id: r.id.toString(), name: `${r.project_namespace}/${r.project_name}`, full_name: `${r.project_namespace}/${r.project_name}`, provider: 'gitlab' });
            }
          }
        } catch { /* no GitLab */ }
        setRepos(allRepos);
        if (allRepos.length > 0) setSelectedRepo(allRepos[0].id);
      } finally {
        setLoadingRepos(false);
      }
    }
  };

  const handleCreatePR = async () => {
    if (!selectedRepo || !filePath) return;
    setCreatingPR(true);
    setPrError('');
    setPrSuccess(null);
    try {
      const repo = repos.find(r => r.id === selectedRepo);
      if (!repo) throw new Error('Repository not found');

      const codeToCommit = displayCode;
      if (repo.provider === 'gitlab') {
        const result = await apiClient.createGitLabMergeRequest({
          repository_id: repo.id,
          target_file: filePath,
          content: codeToCommit,
          mr_title: prTitle,
          mr_description: `Python SDK code for workflow "${workflowName}".\n\nGenerated by Kestrel AI.`,
          commit_message: prTitle,
          update_mode: 'new_file',
          source_type: 'workflow_sdk',
        });
        if (result.success) {
          setPrSuccess({ url: result.merge_request?.url || '' });
        } else {
          setPrError('Failed to create merge request');
        }
      } else {
        const result = await apiClient.createGitHubPullRequest({
          repository_id: repo.id,
          target_file: filePath,
          yaml_content: codeToCommit,
          pr_title: prTitle,
          pr_description: `Python SDK code for workflow "${workflowName}".\n\nGenerated by Kestrel AI.`,
          commit_message: prTitle,
          update_mode: 'new_file',
          source_type: 'workflow_sdk',
        });
        if (result.success) {
          setPrSuccess({ url: result.pull_request?.url || '' });
        } else {
          setPrError('Failed to create pull request');
        }
      }
    } catch (err: any) {
      setPrError(err?.response?.data?.message || err?.message || 'Failed to create PR');
    } finally {
      setCreatingPR(false);
    }
  };

  const theme = resolvedTheme === 'dark' ? oneDark : oneLight;

  return (
    <div className="border-l bg-background flex flex-col h-full min-w-0">
      <div className="flex items-center justify-between p-3 border-b">
        <div className="flex items-center gap-2">
          <Code2 className="w-4 h-4" />
          <h3 className="font-semibold text-sm">Python SDK Code</h3>
          {streaming && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRegenerate}
            disabled={streaming}
            className="h-7 w-7 p-0"
            title="Regenerate"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleEditToggle}
            disabled={!code || streaming}
            className="h-7 w-7 p-0"
            title={editing ? 'Save edits' : 'Edit code'}
          >
            {editing ? <Save className="w-3.5 h-3.5" /> : <Pencil className="w-3.5 h-3.5" />}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopy}
            disabled={!code || streaming}
            className="h-7 w-7 p-0"
            title="Copy to clipboard"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0" title="Close">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-auto">
        {error && !code && (
          <div className="p-4 text-sm text-destructive">{error}</div>
        )}
        {!error && !code && streaming && (
          <div className="p-4 text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Generating Python SDK code...
          </div>
        )}
        {!error && !code && !streaming && (
          <div className="p-4 text-sm text-muted-foreground text-center mt-8">
            <Code2 className="w-8 h-8 mx-auto mb-3 opacity-40" />
            <p>Click the <strong>regenerate</strong> button above to generate<br />Python SDK code for this workflow.</p>
          </div>
        )}
        {code && !editing && (
          <div className="sdk-code-wrapper" style={{ minHeight: '100%' }}>
            <style>{`
              .sdk-code-wrapper pre {
                margin: 0 !important; padding: 16px !important;
                border-radius: 0 !important; border: none !important;
                overflow-x: hidden !important; white-space: pre-wrap !important;
                word-break: break-word !important; font-size: 12px !important;
                line-height: 1.6 !important; min-height: 100% !important;
                background: hsl(var(--background)) !important;
              }
              .sdk-code-wrapper pre code { white-space: pre-wrap !important; word-break: break-word !important; overflow-wrap: break-word !important; background: transparent !important; }
              .sdk-code-wrapper pre code span { white-space: pre-wrap !important; background: transparent !important; }
            `}</style>
            <SyntaxHighlighter language="python" style={theme}>
              {code}
            </SyntaxHighlighter>
          </div>
        )}
        {code && editing && (
          <textarea
            ref={textareaRef}
            value={editedCode}
            onChange={e => setEditedCode(e.target.value)}
            className="w-full h-full p-4 text-xs font-mono bg-background text-foreground resize-none outline-none border-none"
            style={{ minHeight: '100%', lineHeight: '1.6' }}
            spellCheck={false}
          />
        )}
      </div>

      {code && !streaming && !error && (
        <div className="p-2 border-t flex items-center justify-between">
          <span className="text-[11px] text-muted-foreground">
            Install: <code className="text-xs">pip install kestrel-sdk</code>
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={handleOpenPRDialog}
            disabled={!code}
            className="h-7 gap-1.5 text-xs"
          >
            <GitPullRequest className="w-3 h-3" />
            Create PR
          </Button>
        </div>
      )}

      {/* PR Creation Dialog */}
      <Dialog open={prDialogOpen} onOpenChange={setPrDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">Create Pull Request</DialogTitle>
          </DialogHeader>

          {prSuccess ? (
            <div className="space-y-3">
              <Alert>
                <AlertDescription className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-600 shrink-0" />
                  Pull request created successfully.
                </AlertDescription>
              </Alert>
              {prSuccess.url && (
                <Button variant="outline" size="sm" className="w-full" onClick={() => window.open(prSuccess.url, '_blank')}>
                  Open Pull Request
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-medium">Repository</Label>
                {loadingRepos ? (
                  <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading repositories...
                  </div>
                ) : repos.length === 0 ? (
                  <p className="text-xs text-muted-foreground mt-1">No connected repositories. Connect GitHub or GitLab in Integrations.</p>
                ) : (
                  <Select value={selectedRepo} onValueChange={setSelectedRepo}>
                    <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue placeholder="Select repository" /></SelectTrigger>
                    <SelectContent>
                      {repos.map(r => (
                        <SelectItem key={r.id} value={r.id}>
                          <span className="flex items-center gap-1.5">
                            <span className="text-[10px] text-muted-foreground uppercase">{r.provider}</span>
                            {r.full_name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div>
                <Label className="text-xs font-medium">File Path</Label>
                <Input
                  value={filePath}
                  onChange={e => setFilePath(e.target.value)}
                  className="mt-1 h-8 text-sm font-mono"
                  placeholder="workflows/my-workflow.py"
                />
              </div>

              <div>
                <Label className="text-xs font-medium">PR Title</Label>
                <Input
                  value={prTitle}
                  onChange={e => setPrTitle(e.target.value)}
                  className="mt-1 h-8 text-sm"
                  placeholder="Add workflow: My Workflow"
                />
              </div>

              {prError && (
                <Alert variant="destructive">
                  <AlertDescription className="text-xs">{prError}</AlertDescription>
                </Alert>
              )}
            </div>
          )}

          <DialogFooter>
            {!prSuccess && (
              <Button
                onClick={handleCreatePR}
                disabled={creatingPR || !selectedRepo || !filePath || repos.length === 0}
                size="sm"
                className="gap-1.5"
              >
                {creatingPR ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <GitPullRequest className="w-3.5 h-3.5" />}
                {creatingPR ? 'Creating...' : 'Create PR'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
